import React, { useRef, useState, useEffect } from "react";
import { View, Platform } from "react-native";
import { WebView } from "react-native-webview";

interface VideoPlayerProps {
  videoUrl: string;
  watchProgress: { position: number; watched: boolean };
  onProgressUpdate: (highestWatchPosition: number, watched: boolean) => void;
  duration: number;
}

const PdrVideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, watchProgress, onProgressUpdate, duration }) => {
  let Video;
  if (Platform.OS !== "web") {
    ({ default: Video } = require("react-native-video"));
  }
  const { position = 0, watched = false } = watchProgress || { position: 0, watched: false };
  const [showWebView, setShowWebView] = useState(false);
  const videoRef = useRef<typeof Video>(null);
  const webViewRef = useRef<WebView>(null);
  const [highestWatchPosition, setHighestWatchPosition] = useState(watched ? duration : position);
  const [videoWatched, setVideoWatched] = useState(watched);
  const [lastUpdateTimeStamp, setLastUpdateTimeStamp] = useState(Date.now());

  const highestWatchPositionRef = useRef(highestWatchPosition);
  const videoWatchedRef = useRef(videoWatched);
  const durationRef = useRef(duration);

  useEffect(() => {
    if (Platform.OS !== "web") {
      setTimeout(() => setShowWebView(true), 500);
    }
  }, []);

  const updateHighestPosition = (currentTime: number) => {
    const roundedTime = Math.floor(currentTime);
    if (roundedTime > highestWatchPosition) {
      setHighestWatchPosition(roundedTime);
      if (Date.now() - lastUpdateTimeStamp > 5000) {
        const isWatched = videoWatched || duration - roundedTime <= 6;
        setVideoWatched(isWatched);
        onProgressUpdate(roundedTime, isWatched);
        setLastUpdateTimeStamp(Date.now());
      }
    }
  };

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body, html { margin: 0; padding: 0; height: 100%; width: 100%; }
          video { width: 100%; height: 100%; object-fit: contain; }
        </style>
      </head>
      <body>
        <video id="videoPlayer" controls playsinline preload="metadata">
          <source src="${videoUrl}" type="video/mp4">
          Your browser does not support the video tag.
        </video>
        <script>
          const video = document.getElementById('videoPlayer');
          let highestWatchPosition = ${highestWatchPositionRef.current};
          let watched = ${videoWatchedRef.current};
          let duration = ${durationRef.current};
          video.currentTime = highestWatchPosition;

          video.addEventListener('timeupdate', function() {
            if (this.currentTime > highestWatchPosition + 2) {
              this.currentTime = highestWatchPosition;
            } else if (this.currentTime > highestWatchPosition) {
              highestWatchPosition = this.currentTime;
              if (!watched && this.currentTime >= duration - 6) {
                watched = true;
              }
            }
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'progress', time: this.currentTime }));
          });

          video.addEventListener('seeking', function() {
            if (!watched && this.currentTime > highestWatchPosition) {
              this.currentTime = highestWatchPosition;
            }
          });

          window.addEventListener('message', (event) => {
            const data = JSON.parse(event.data);
            if (data.type === 'updateVariables') {
              updateVariables(data);
            }
          });
        </script>
      </body>
    </html>
  `;

  if (Platform.OS === "web") {
    return (
      <View className="aspect-video w-full sm:px-6">
        <video
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => {
            e.currentTarget.currentTime = position;
          }}
          onTimeUpdate={(e) => {
            if (e.currentTarget.currentTime > highestWatchPosition + 2) {
              e.currentTarget.currentTime = highestWatchPosition;
            }
            updateHighestPosition(e.currentTarget.currentTime);
          }}
          onSeeking={(e) => {
            if (!videoWatched && e.currentTarget.currentTime > highestWatchPosition) {
              e.currentTarget.currentTime = highestWatchPosition;
            }
          }}
          className="w-full h-full object-contain"
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </View>
    );
  }

  return (
    <View className="w-full h-full" testID="pdr-video-player">
      <Video
        ref={videoRef}
        source={{ uri: videoUrl }}
        className="absolute top-0 left-0 bottom-0 right-0"
        paused={true}
        resizeMode="contain"
      />
      {showWebView && (
        <WebView
          ref={webViewRef}
          source={{ html }}
          style={{ flex: 1 }}
          javaScriptEnabled={true}
          domStorageEnabled={true}
          allowsFullscreenVideo={true}
          allowsInlineMediaPlayback={true}
          mediaPlaybackRequiresUserAction={false}
          onMessage={(event: any) => {
            const data = JSON.parse(event.nativeEvent.data);
            if (data.type === "progress") {
              updateHighestPosition(data.time);
            }
          }}
        />
      )}
    </View>
  );
};

export default PdrVideoPlayer;
